import { useMemo, useState } from 'react';
import type { FormQuestion, FormAnswer, Event } from '~/lib/Api.types';
import useSaveFormSubmission from '~/lib/useSaveFormSubmission';
import EditFormSubmission from '../forms/submissions/EditFormSubmission';
import FormAnswers from '../forms/submissions/FormAnswers';
import ClipboardText from '../icons/ClipboardText';
import PencilSquareIcon from '../icons/PencilSquareIcon';
import SectionHeading from '../SectionHeading';
import { QuestionType } from '~/lib/Questions/Questions.constants';
import SignatureHeader from '../SignatureHeader';

type EventFormAnswersProps = {
    answers: Array<{
        question: FormQuestion;
        answer: FormAnswer;
    }>;
    event?: Event;
    refetch: () => void;
};

const EventFormAnswers = ({
    answers,
    event,
    refetch,
}: EventFormAnswersProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const { newAnswers, setNewAnswers, isSaving, doSave } =
        useSaveFormSubmission(event?.form_submission_id, async () => {
            await refetch();
            setIsEditing(false);
        });

    const answersIncludeSignature = useMemo(() => {
        return (
            answers.some(
                ({ question }) => question.type === QuestionType.Signature,
            ) ?? false
        );
    }, [answers]);

    const formSubmissionIsEditable =
        answers.length > 0 && !answersIncludeSignature;

    const showSignatureLock = answersIncludeSignature;

    return (
        <>
            <SectionHeading
                icon={<ClipboardText />}
                name={
                    <div className="flex items-center justify-between flex-row">
                        Forms
                        <div className="flex flex-row items-center">
                            {formSubmissionIsEditable && (
                                <button
                                    type="button"
                                    className="btn-light-pill-sm uppercase ml-2"
                                    onClick={() => setIsEditing(!isEditing)}
                                >
                                    Edit
                                </button>
                            )}
                            {showSignatureLock && event?.form_submission_id && (
                                <SignatureHeader
                                    formSubmissionId={event?.form_submission_id}
                                />
                            )}
                        </div>
                    </div>
                }
                className="mt-8"
            />
            <div className="panel px-6 py-4 mt-2">
                {isEditing && (
                    <form
                        onSubmit={ev => {
                            ev.preventDefault();
                            doSave();
                        }}
                    >
                        <EditFormSubmission
                            currentQuestionsAndAnswers={answers}
                            newAnswers={newAnswers}
                            setNewAnswers={setNewAnswers}
                        />
                        <div className="flex items-center  mt-4">
                            <button className="btn-save">
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                className="btn-gray-pill-lg ml-4"
                                type="button"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
                {!isEditing && (
                    <div>
                        <FormAnswers answers={answers} />
                    </div>
                )}
            </div>
        </>
    );
};
export default EventFormAnswers;
